import {useManifestPrintoutLayoutsQuery} from "./graphql";
import {ManifestPrintoutLayoutListing} from "../../__generated__/graphql/types";
import {Button, Card, Checkbox, Col, Drawer, Icon, List, Row, Tooltip, Typography} from "antd";
import PlaceholderImage from '../../img/manifest-printout-placeholder.svg';
import cn from 'classnames';
import './layout-selector.style.less';
import React, {useEffect, useReducer, useState} from "react";
import NonIdealState from "../NonIdealState";
import useBreakpoints from "../../hooks/useBreakpoints";

const BASE_CLS = 'mc-manifest-printout-layout-selector';

export interface ManifestPrintoutLayoutSelectProps {
    selectedClassName?: string,
    onChange?: (selectedClassName: string) => void,
    manifestType: string,

    /**
     * If you always want the first item to be selected, set this option to true.
     */
    selectFirstByDefault?: boolean,

    /**
     * Text to show when no layouts are found.
     */
    noLayoutsFoundText?: string
}

const DefaultState = {
    expandedImageUrl: null as string,
    title: null as string,
    layoutClass: null as string,
    drawerOpen: false
}

type Action = {
    type: 'EXPAND_IMAGE',
    url: string,
    title: string,
    layoutClass: string
} | {
    type: 'CLOSE'
}

const reducer: React.Reducer<typeof DefaultState, Action> = (state, action) => {
    switch (action.type) {
        case "EXPAND_IMAGE":
            return {
                expandedImageUrl: action.url,
                title: action.title,
                drawerOpen: true,
                layoutClass: action.layoutClass
            }
        case "CLOSE":
            return {
                ...state,
                drawerOpen: false
            }
    }
}

export default function ManifestPrintoutLayoutSelect(props: ManifestPrintoutLayoutSelectProps){
    const { layouts, ...result } = useManifestPrintoutLayoutsQuery(props.manifestType);
    const bp = useBreakpoints();

    const [ state, dispatch ] = useReducer(reducer, DefaultState);

    useEffect(() => {
        if (layouts.length && props.selectFirstByDefault){
            const firstLayout = layouts[0];
            props.onChange?.(firstLayout.layoutClass);
        }
    }, [layouts.length, props.selectFirstByDefault]);

    function selectLayout(layoutClass: string){
        props.onChange?.(layoutClass);
    }

    function renderLayout(layout: ManifestPrintoutLayoutListing){

        function handleClick(){
            selectLayout(layout.layoutClass);
        }

        const isSelected = props.selectedClassName === layout.layoutClass

        const title = <Row gutter={12} type="flex">
            <Col>
                <Checkbox
                    checked={isSelected}
                    onClick={handleClick}
                />
            </Col>
            <Col>
                {layout.layoutName}
            </Col>
        </Row>

        return (
            <List.Item key={layout.layoutClass}>
                <Card
                    hoverable
                    size="small"
                    title={title}
                    className={cn({
                        [BASE_CLS + '-card']: true,
                        [BASE_CLS + '-card-selected']: isSelected
                    })}
                    onClick={handleClick}
                    bodyStyle={{
                        backgroundImage: `url(${layout.previewImageUrl || PlaceholderImage})`,
                        backgroundPosition: 'top',
                        backgroundSize: 'cover',
                        height: 250,
                        display: 'flex',
                        justifyContent: 'end',
                        alignItems: 'end'
                    }}
                >
                    {layout.previewImageUrl ? (
                        <Tooltip title="Expand image">
                            <Button
                                icon="arrows-alt"
                                type="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch({
                                        type: 'EXPAND_IMAGE',
                                        url: layout.previewImageUrl,
                                        title: layout.layoutName,
                                        layoutClass: layout.layoutClass
                                    })
                                }}
                            />
                        </Tooltip>
                    ) : null}
                </Card>
            </List.Item>
        )
    }

    const renderedLayouts = layouts.map(renderLayout);

    const noLayouts = <List.Item key="no-layouts">
        <Card
            size="small"
        >
            <NonIdealState
                title="No layouts available"
                description={props.noLayoutsFoundText}
                icon="question-circle"
                style={{ height: 250 }}
            />
        </Card>
    </List.Item>

    const layoutError = <List.Item key="no-layouts">
        <Card
            size="small"
        >
            <NonIdealState
                title="Failed to load layouts"
                description="Check console for details"
                icon={<Typography.Text type="danger">
                    <Icon type="exclamation-circle" style={{ fontSize: '4rem', marginBottom: '1rem' }} />
                </Typography.Text>}
                style={{ height: 250 }}
            />
        </Card>
    </List.Item>

    const layoutLoading = <List.Item key="no-layouts">
        <Card
            size="small"
            bodyStyle={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <NonIdealState
                title="Loading layouts"
                icon="loading"
                style={{ height: 250 }}
            />
        </Card>
    </List.Item>

    let layoutList: JSX.Element[] = [layoutLoading];

    if (result.error){
        layoutList = [layoutError];
    }
    else if (!layouts.length && !result.loading){
        layoutList = [noLayouts];
    }
    else if (layouts.length) {
        layoutList = renderedLayouts;
    }

    return <>
        <List
            grid={{
                gutter: 16,
                xs: 1,
                sm: 2,
                md: 4,
                lg: 4
            }}
            dataSource={layoutList}
            renderItem={(item) => item}
        />
        <Drawer
            visible={state.drawerOpen}
            onClose={() => dispatch({ type: 'CLOSE' })}
            width={bp.min("medium") ? 848 : '100%'}
            className="ant-drawer-flex"
            title={
                <Row type="flex">
                    <Col>
                        <span>{`${state.title} (example)`}</span>
                    </Col>
                    {state.layoutClass !== props.selectedClassName ? (
                        <Col style={{ marginLeft: 'auto', marginRight: '2rem', marginBottom: -3, marginTop: -4 }}>
                            <Button
                                type="primary"
                                onClick={() => {
                                    selectLayout(state.layoutClass);
                                    dispatch({ type: 'CLOSE' });
                                }}
                            >Select</Button>
                        </Col>
                    ) : null}
                </Row>
            }
        >
            {state.expandedImageUrl ? (
                <img
                    alt="Full layout image"
                    src={state.expandedImageUrl}
                    width={800}
                />
            ) : null}
        </Drawer>
    </>
}